import React, { useEffect } from "react";
import { guardianAlertService } from "../../services/guardianAlertService";
import {
  Overlay,
  ModalContainer,
  ModalHeader,
  HeaderText,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  ModalBody,
  Section,
  SectionTitle,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  DescriptionBlock,
  DescriptionText,
  BadgeRow,
  StatusBadge,
  ModalFooter,
  FooterButton,
} from "./styles";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch {
    return String(value);
  }
}

function getResolutionMeta(resolution) {
  if (resolution === "created") {
    return { label: "Alerta Criado", tone: "success" };
  }
  if (resolution === "rejected") {
    return { label: "Rejeitado", tone: "danger" };
  }
  return { label: "Pendente", tone: "warning" };
}

function getSourceLabel(source) {
  if (source === "firms_automated") return "FIRMS Automático";
  if (source === "manual") return "Manual";
  return source || "—";
}

/**
 * Modal de detalhes da solicitação de alerta.
 * Ações: Criar alerta, Rejeitar, Marcar como recebido, Ver Alerta (se resolution=created).
 */
export default function RequestModal({
  req,
  onClose,
  onCreateAlert,
  onReject,
  onViewAlert,
  viewingAlert = false,
  markingReceived = false,
  onMarkedReceived,
  canCreateAlert = true,
}) {
  useEffect(() => {
    if (!req) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [req, onClose]);

  if (!req) return null;

  const area = req.area_detail;
  const user = req.requested_by;
  const alertType = req.alert_type_detail;
  const resolution = getResolutionMeta(req.resolution);
  const isResolved = Boolean(req.resolution);
  const isCreated = req.resolution === "created";

  const handleMarkReceived = async () => {
    try {
      await guardianAlertService.markAsReceived(req.id, user?.id);
      const next = {
        ...req,
        received: true,
        received_at: new Date().toISOString(),
        received_by_id: user?.id,
      };
      onMarkedReceived?.(next);
      onClose?.();
    } catch (err) {
      console.error("Erro ao marcar como recebido:", err);
      window.alert("Erro ao marcar como recebido.");
    }
  };

  return (
    <Overlay
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <ModalContainer
        role="dialog"
        aria-modal="true"
        aria-labelledby="alert-request-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <HeaderText>
            <ModalTitle id="alert-request-modal-title">
              Solicitação de alerta
            </ModalTitle>
            <ModalSubtitle>
              {req.cod_alerta ? (
                <>
                  Código <strong>{req.cod_alerta}</strong>
                  {alertType?.description ? ` · ${alertType.description}` : ""}
                </>
              ) : (
                alertType?.description || "Solicitação de alerta"
              )}
            </ModalSubtitle>
            <BadgeRow>
              <StatusBadge $tone={resolution.tone}>
                Resolução: {resolution.label}
              </StatusBadge>
              <StatusBadge $tone={req.received ? "success" : "warning"}>
                {req.received ? "Recebido" : "Recebimento pendente"}
              </StatusBadge>
              <StatusBadge $tone="info">
                {getSourceLabel(req.request_source)}
              </StatusBadge>
            </BadgeRow>
          </HeaderText>
          <CloseButton type="button" onClick={onClose} aria-label="Fechar">
            ✕
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <Section>
            <SectionTitle>Informações do alerta</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Código</InfoLabel>
                <InfoValue>{req.cod_alerta || "—"}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Tipo</InfoLabel>
                <InfoValue>{alertType?.description || "—"}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Local</InfoLabel>
                <InfoValue>{req.location || "—"}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Solicitado em</InfoLabel>
                <InfoValue>{formatDate(req.requested_at)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Último lembrete</InfoLabel>
                <InfoValue>{formatDate(req.last_reminder_sent_at)}</InfoValue>
              </InfoItem>
            </InfoGrid>

            <DescriptionBlock>
              <InfoLabel>Descrição</InfoLabel>
              <DescriptionText>{req.description || "—"}</DescriptionText>
            </DescriptionBlock>

            {req.other_description ? (
              <DescriptionBlock>
                <InfoLabel>Descrição complementar</InfoLabel>
                <DescriptionText>{req.other_description}</DescriptionText>
              </DescriptionBlock>
            ) : null}
          </Section>

          {(area || area?.project_detail?.title) && (
            <Section>
              <SectionTitle>Área / Projeto</SectionTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Área</InfoLabel>
                  <InfoValue>{area?.name || "—"}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Projeto</InfoLabel>
                  <InfoValue>{area?.project_detail?.title || "—"}</InfoValue>
                </InfoItem>
              </InfoGrid>
            </Section>
          )}

          {user ? (
            <Section>
              <SectionTitle>Solicitante</SectionTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Nome</InfoLabel>
                  <InfoValue>{user.full_name || "—"}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>E-mail</InfoLabel>
                  <InfoValue>{user.email || "—"}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Telefone</InfoLabel>
                  <InfoValue>{user.phone || "—"}</InfoValue>
                </InfoItem>
              </InfoGrid>
            </Section>
          ) : null}
        </ModalBody>

        <ModalFooter>
          <FooterButton type="button" $variant="ghost" onClick={onClose}>
            Fechar
          </FooterButton>

          {!req.received ? (
            <FooterButton
              type="button"
              $variant="ghost"
              disabled={markingReceived}
              onClick={handleMarkReceived}
            >
              {markingReceived ? "Marcando…" : "Marcar como recebido"}
            </FooterButton>
          ) : null}

          <FooterButton
            type="button"
            $variant="reject"
            disabled={isResolved}
            onClick={() => onReject?.(req)}
          >
            Rejeitar
          </FooterButton>

          {isCreated ? (
            <FooterButton
              type="button"
              disabled={viewingAlert}
              onClick={() => onViewAlert?.(req)}
            >
              {viewingAlert ? "Abrindo…" : "Ver Alerta"}
            </FooterButton>
          ) : canCreateAlert ? (
            <FooterButton
              type="button"
              disabled={isResolved}
              onClick={() => onCreateAlert?.(req)}
            >
              Criar alerta
            </FooterButton>
          ) : null}
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
}
